import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface ColumnMapping {
  timestamp: string
  open: string
  high: string
  low: string
  close: string
  volume?: string
}

interface ColumnMappingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  csvColumns: string[]
  onConfirm: (mapping: ColumnMapping) => void
  onCancel: () => void
}

const REQUIRED_FIELDS = ['timestamp', 'open', 'high', 'low', 'close'] as const
const OPTIONAL_FIELDS = ['volume'] as const
const NONE_VALUE = '__none__' // Special value to represent "no selection" (cannot be empty string)

export function ColumnMappingModal({
  open,
  onOpenChange,
  csvColumns,
  onConfirm,
  onCancel,
}: ColumnMappingModalProps) {
  const { t } = useTranslation()
  const [mapping, setMapping] = useState<Partial<ColumnMapping>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Auto-detect column mappings based on common names
  useEffect(() => {
    if (csvColumns.length === 0) return

    const autoMapping: Partial<ColumnMapping> = {}

    // Auto-detect timestamp
    const timestampPatterns = ['time', 'timestamp', 'date', 'datetime']
    for (const pattern of timestampPatterns) {
      const found = csvColumns.find(
        (col) => col.toLowerCase().trim() === pattern || col.toLowerCase().trim().includes(pattern)
      )
      if (found) {
        autoMapping.timestamp = found
        break
      }
    }

    // Auto-detect OHLCV
    const ohlcvMap: Record<string, string[]> = {
      open: ['open', 'o'],
      high: ['high', 'h', 'max'],
      low: ['low', 'l', 'min'],
      close: ['close', 'c', 'last'],
      volume: ['volume', 'vol', 'v', 'qty', 'quantity'],
    }

    for (const [field, patterns] of Object.entries(ohlcvMap)) {
      for (const pattern of patterns) {
        const found = csvColumns.find(
          (col) => col.toLowerCase().trim() === pattern || col.toLowerCase().trim().includes(pattern)
        )
        if (found && !Object.values(autoMapping).includes(found)) {
          autoMapping[field as keyof ColumnMapping] = found
          break
        }
      }
    }

    setMapping(autoMapping)
  }, [csvColumns])

  const handleFieldChange = (field: keyof ColumnMapping, value: string) => {
    setMapping((prev) => ({
      ...prev,
      [field]: value === NONE_VALUE ? undefined : value,
    }))
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateMapping = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Check required fields
    for (const field of REQUIRED_FIELDS) {
      if (!mapping[field]) {
        newErrors[field] = t('datasets.columnMapping.required', { defaultValue: 'This field is required' })
      }
    }

    // Check for duplicate mappings
    const usedColumns = Object.values(mapping).filter(Boolean)
    const duplicates = usedColumns.filter(
      (col, index) => usedColumns.indexOf(col) !== index
    )
    if (duplicates.length > 0) {
      newErrors._general = t('datasets.columnMapping.duplicate', {
        defaultValue: 'Each column can only be mapped to one field',
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleConfirm = () => {
    if (!validateMapping()) {
      return
    }

    onConfirm(mapping as ColumnMapping)
    onOpenChange(false)
  }

  const fieldLabels: Record<string, string> = {
    timestamp: t('datasets.columnMapping.timestamp', { defaultValue: 'Timestamp' }),
    open: t('datasets.columnMapping.open', { defaultValue: 'Open' }),
    high: t('datasets.columnMapping.high', { defaultValue: 'High' }),
    low: t('datasets.columnMapping.low', { defaultValue: 'Low' }),
    close: t('datasets.columnMapping.close', { defaultValue: 'Close' }),
    volume: t('datasets.columnMapping.volume', { defaultValue: 'Volume (optional)' }),
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('datasets.columnMapping.title', { defaultValue: 'Map CSV Columns' })}
          </DialogTitle>
          <DialogDescription>
            {t('datasets.columnMapping.description', {
              defaultValue: 'Select which CSV columns correspond to each required field',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors._general && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errors._general}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {REQUIRED_FIELDS.map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field}>
                  {fieldLabels[field]} <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={mapping[field] || NONE_VALUE}
                  onValueChange={(value) => handleFieldChange(field, value)}
                >
                  <SelectTrigger id={field} className={errors[field] ? 'border-destructive' : ''}>
                    <SelectValue placeholder={t('datasets.columnMapping.selectColumn', { defaultValue: 'Select column...' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t('datasets.columnMapping.none', { defaultValue: 'None' })}</SelectItem>
                    {csvColumns.map((col) => (
                      <SelectItem key={col} value={col}>
                        {col}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors[field] && (
                  <p className="text-sm text-destructive">{errors[field]}</p>
                )}
              </div>
            ))}

            {OPTIONAL_FIELDS.map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field}>{fieldLabels[field]}</Label>
                <Select
                  value={mapping[field] || NONE_VALUE}
                  onValueChange={(value) => handleFieldChange(field, value)}
                >
                  <SelectTrigger id={field}>
                    <SelectValue placeholder={t('datasets.columnMapping.selectColumn', { defaultValue: 'Select column...' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t('datasets.columnMapping.none', { defaultValue: 'None' })}</SelectItem>
                    {csvColumns.map((col) => (
                      <SelectItem key={col} value={col}>
                        {col}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleConfirm}>
            {t('common.confirm', { defaultValue: 'Confirm' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
